import { useState, useEffect, useCallback } from 'react';
import { useClient } from '../../../hooks/useClient';
import { useAuthContext } from '../../../context/AuthContext';
import useSWR from 'swr';

interface UnreadTicketsResponse {
  unreadTicketIds: string[];
}

const POLLING_INTERVAL = 30000; // 30 segundos

export function useUnreadTickets() {
  const { get, post } = useClient();
  const { user } = useAuthContext();
  const [unreadTicketIds, setUnreadTicketIds] = useState<string[]>([]);

  const url = user?.organizationId
    ? `/organizations/${user.organizationId}/tickets/unread`
    : null;

  const { data, mutate } = useSWR<UnreadTicketsResponse>(
    url,
    async (url: string) => {
      const response = await get<UnreadTicketsResponse>(url);
      if (!response) throw new Error('Failed to fetch unread tickets');
      return response;
    },
    {
      refreshInterval: POLLING_INTERVAL,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    },
  );

  useEffect(() => {
    if (data?.unreadTicketIds) {
      setUnreadTicketIds(data.unreadTicketIds);
    } else {
      setUnreadTicketIds([]);
    }
  }, [data]);

  const isUnread = useCallback(
    (ticketId: string) => {
      return unreadTicketIds.includes(ticketId);
    },
    [unreadTicketIds],
  );

  const markAsRead = useCallback(
    // Only the ticket detail calls it, once the organization is loaded. useClient reports a failed request itself.
    async (cardId: string) => {
      await post(`/organizations/${user?.organizationId}/tickets/${cardId}/mark-read`, {
        handlers: {
          defaultSuccess: () => {
            // Actualizar el estado local inmediatamente para quitar el dot
            setUnreadTicketIds((prev) => prev.filter((id) => id !== cardId));
            // Refrescar la lista completa después de un pequeño delay
            setTimeout(() => {
              mutate();
            }, 500);
          },
        },
      });
    },
    [user?.organizationId, mutate],
  );

  return {
    unreadTicketIds,
    unreadCount: unreadTicketIds.length,
    isUnread,
    markAsRead,
  };
}
